import React from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { packageSizeOptions, PackageSizeFormValues } from './PackageSizeCard';

export interface OrderBoxValue {
  size: PackageSizeFormValues;
}

export interface BoxFeeBreakdown {
  sequence: number;
  fee: number;
  charged: number;
  is_primary?: boolean;
  /** BOXY-301 (owner 2026-10-22): the main box carries the order fee, every other box a flat extra-box fee. */
  role?: 'main' | 'extra';
}

/** MIN 2 / MAX 20 mirror the backend (`min:2|max:20`, config/multi_box.max_boxes). */
export const MIN_BOXES = 2;
export const MAX_BOXES = 20;

const SIZE_RANK: Record<PackageSizeFormValues, number> = { XS: 1, S: 2, M: 3, L: 4, XL: 5 };

/** The main box, charged the order fee: the largest, first one wins a tie (as the backend). */
export const primaryIndex = (boxes: OrderBoxValue[]) =>
  boxes.reduce((best, box, i) => (SIZE_RANK[box.size] > SIZE_RANK[boxes[best].size] ? i : best), 0);

export const largestSize = (boxes: OrderBoxValue[]) => boxes[primaryIndex(boxes)]?.size ?? 'S';

const roleOf = (row: BoxFeeBreakdown): 'main' | 'extra' =>
  row.role ?? (row.is_primary ? 'main' : 'extra');

interface MultiBoxSelectorProps {
  value?: OrderBoxValue[];
  onChange?: (boxes: OrderBoxValue[]) => void;
  /** Per-box figures from the price preview. Absent until a preview lands. */
  breakdown?: BoxFeeBreakdown[];
  disabled?: boolean;
}

/**
 * BOXY-301 §5.1 — the "boxes have different sizes" list: one line per box,
 * each line the label it will carry (BOX n/N). The box COUNT is owned by the
 * stepper in PackageSizeCard; this list only edits sizes and removes a box.
 */
const MultiBoxSelector: React.FC<MultiBoxSelectorProps> = ({
  value,
  onChange,
  breakdown,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const boxes =
    value && value.length >= MIN_BOXES ? value : [{ size: 'S' as const }, { size: 'S' as const }];
  const setSize = (index: number, size: PackageSizeFormValues) =>
    onChange?.(boxes.map((box, i) => (i === index ? { size } : box)));
  const removeBox = (index: number) => onChange?.(boxes.filter((_, i) => i !== index));

  return (
    <ul className='mb-0 list-none space-y-2 ps-0' data-testid='multi-box-selector'>
      {boxes.map((box, index) => {
        const row = breakdown?.find((r) => r.sequence === index + 1);

        return (
          <li
            key={index}
            className='flex items-center gap-2 rounded-lg border border-solid border-sd-input bg-sd-card px-3 py-2'
          >
            {/* Literal "BOX n/N" in every language: it is what the printed label says. */}
            <span className='shrink-0 whitespace-nowrap text-xs font-semibold tabular-nums text-sd-foreground'>
              {`BOX ${index + 1}/${boxes.length}`}
            </span>

            <Select
              value={box.size}
              onValueChange={(size) => setSize(index, size as PackageSizeFormValues)}
              disabled={disabled}
            >
              <SelectTrigger
                aria-label={String(t('box_n', { number: index + 1 }))}
                className='h-8 min-w-0 flex-1 rounded-lg border border-solid border-sd-input bg-sd-input-bg text-xs font-semibold'
              >
                <SelectValue>
                  {String(t(packageSizeOptions.find((o) => o.key === box.size)?.title ?? ''))}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {packageSizeOptions.map((o) => (
                  <SelectItem key={o.key} value={o.key}>
                    {String(t(o.title))}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* §5.1 (owner 2026-10-22) — each line says whether it is the main
                box (the order fee) or an extra box (flat fee), and its amount. */}
            {row ? (
              <>
                <span className='shrink-0 text-[10px] font-medium text-sd-muted-foreground'>
                  {String(t(roleOf(row) === 'main' ? 'box_role_main' : 'box_role_extra'))}
                </span>
                <span className='w-12 shrink-0 text-end text-xs font-semibold tabular-nums text-sd-foreground'>
                  {row.charged.toLocaleString()}
                </span>
              </>
            ) : null}

            {/* Disabled, never hidden, at the minimum: a vanished control
                leaves the merchant wondering what they did. */}
            <button
              type='button'
              aria-label={String(t('remove_box_n', { number: index + 1 }))}
              disabled={disabled || boxes.length <= MIN_BOXES}
              onClick={() => removeBox(index)}
              className='flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-sd-muted-foreground transition-colors hover:bg-sd-muted hover:text-sd-destructive disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-sd-muted-foreground'
            >
              <Trash2 className='h-3.5 w-3.5' />
            </button>
          </li>
        );
      })}
    </ul>
  );
};

/** §5.2 — the main box's fee, what the extra boxes add, and the total. */
export const BoxesPriceSummary: React.FC<{ breakdown?: BoxFeeBreakdown[]; count: number }> = ({
  breakdown,
  count,
}) => {
  const { t } = useTranslation();
  if (!breakdown?.length) return null;

  const total = breakdown.reduce((sum, row) => sum + row.charged, 0);
  const extra = breakdown
    .filter((row) => roleOf(row) === 'extra')
    .reduce((sum, row) => sum + row.charged, 0);
  const main = total - extra;

  return (
    <div className='mt-3 space-y-1.5 rounded-lg bg-sd-muted px-3 py-2.5 text-xs'>
      <div className='flex justify-between text-sd-muted-foreground'>
        <span>{String(t('main_box_fee_label'))}</span>
        <span dir='ltr' className='tabular-nums'>
          {main.toLocaleString()}
        </span>
      </div>
      <div className='flex justify-between text-sd-muted-foreground'>
        <span>{String(t('extra_boxes_fee_label', { count: breakdown.length - 1 }))}</span>
        <span dir='ltr' className='tabular-nums'>
          {`+${extra.toLocaleString()}`}
        </span>
      </div>
      <div className='flex justify-between font-semibold text-sd-foreground'>
        <span>{String(t('shipping_for_n_boxes', { count }))}</span>
        <span dir='ltr' className='tabular-nums'>
          {total.toLocaleString()}
        </span>
      </div>
    </div>
  );
};

export default MultiBoxSelector;
